import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '../lib/api.js';
import { useSession } from '../App.jsx';
import { parseNumber } from '../lib/numbers.js';
import { ROLES, can } from '../lib/roles.js';
import { ago, count, day, inr, stamp } from '../lib/format.js';
import { ActivityBars } from '../components/charts.jsx';
import MarkInTally from '../components/MarkInTally.jsx';
import { Alert, Card, CardSkeleton, Empty, Field, LoadMore, Pill, StatCard, useAsync, usePagedList } from '../components/ui.jsx';

const INTERVALS = [300, 600, 900, 1800, 3600, 14400, 86400];
const intervalLabel = (s) => (s < 3600 ? `Every ${s / 60} minutes` : s === 3600 ? 'Every hour' : s === 86400 ? 'Once a day' : `Every ${s / 3600} hours`);
// ?jobs=failed (from the failed-sync alert) opens the job queue on that view.
const JOB_VIEWS = [['', 'All'], ['queued,leased', 'In flight'], ['failed', 'Failed']];

const FLAGS = [
  ['pull_masters', 'Pull masters', 'Parties, items, godowns, HSN and GST rates'],
  ['pull_purchases', 'Pull purchases', 'Creates batches with their godown and supplier'],
  ['pull_stock', 'Pull stock', 'Batch-wise closing stock per godown'],
  ['push_invoices', 'Push invoices', 'Posts the sales voucher back to Tally'],
];

export default function SyncAdmin() {
  const configs = useAsync(() => api.get('/api/sync/config'), []);
  const agents = useAsync(() => api.get('/api/sync/agents'), []);
  const session = useSession();
  const isAdmin = can(session, ROLES.ADMIN);
  const [params] = useSearchParams();
  const [jobView, setJobView] = useState(() => {
    const asked = params.get('jobs') || '';
    return JOB_VIEWS.some(([key]) => key === asked) ? asked : '';
  });
  const jobs = usePagedList(`/api/sync/jobs?limit=12${jobView ? `&status=${jobView}` : ''}`, 'jobs');
  const events = useAsync(() => api.get('/api/sync/events?limit=20'), []);
  const notPosted = useAsync(() => api.get('/api/sync/documents/not-posted'), []);
  const activity = useAsync(() => api.get('/api/reports/analytics?days=7'), []);
  const [error, setError] = useState(null);
  const [newToken, setNewToken] = useState(null);
  const [pairing, setPairing] = useState(null);

  const save = async (entityId, patch) => {
    setError(null);
    try { await api.patch(`/api/sync/config/${entityId}`, patch); configs.reload(); }
    catch (e) { setError(e.message); }
  };

  const issueToken = async (entityId) => {
    setError(null);
    try {
      const res = await api.post('/api/sync/agents', { entity_id: entityId, name: `Agent ${day(new Date())}` });
      setNewToken(res); agents.reload();
    } catch (e) { setError(e.message); }
  };

  const pairComputer = async (entityId) => {
    setError(null); setNewToken(null);
    try { setPairing(await api.post('/api/sync/pairing-codes', { entity_id: entityId })); }
    catch (e) { setError(e.message); }
  };

  const queued = configs.data ? configs.data.configs.reduce((a, c) => a + Number(c.queued), 0) : 0;
  const failed = configs.data ? configs.data.configs.reduce((a, c) => a + Number(c.failed), 0) : 0;
  const lastSeen = agents.data
    ? agents.data.agents.map((a) => a.last_seen_at).filter(Boolean).sort().slice(-1)[0]
    : null;

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-title">Tally sync</h1>
          <div className="page-sub">
            The agent on the client's Tally machine polls this queue over an outbound connection. Nothing here
            reaches into their network, and only the sales invoice is ever written to Tally.
          </div>
        </div>
      </div>

      <Alert onRetry={() => setError(null)}>{error}</Alert>
      <Alert onRetry={() => { configs.reload(); agents.reload(); events.reload(); }}>
        {configs.error || agents.error || events.error}
      </Alert>
      {newToken && (
        <Alert kind="ok">
          New agent token — copy it into the agent's <span className="mono">.env</span> now; it is never shown again.
          <div className="mono" style={{ marginTop: 6, userSelect: 'all' }}>{newToken.token}</div>
        </Alert>
      )}

      {pairing && (
        <Card title={`Connect the Tally computer for ${pairing.entity.name}`}
          note="One installer puts the InvIT Connector on the computer where TallyPrime runs. It starts with Windows and has no window of its own."
          actions={<button className="btn small ghost" onClick={() => setPairing(null)}>Done</button>}>
          <ol style={{ margin: 0, paddingLeft: 20, lineHeight: 1.9 }}>
            <li>
              {pairing.download_url
                ? <>Download <a href={pairing.download_url}>InvIT-Connector-Setup.exe</a> on that computer and run it.</>
                : <>Run <b>InvIT-Connector-Setup.exe</b> on that computer (ask Authentic Technologies for it).</>}
            </li>
            <li>When it asks, enter this pairing code:
              <div className="mono token-once" style={{ margin: '6px 0', fontSize: 22, letterSpacing: 2, userSelect: 'all' }}>{pairing.code}</div>
              <span className="card-note">Works once, until {stamp(pairing.expires_at)}. InvIT address: <span className="mono">{pairing.api_url}</span></span>
            </li>
            <li>Keep TallyPrime open with the company selected, and its XML server on (port 9000). The Connector
              works with whichever company is selected in Tally; there is nothing to set.
              The computer appears under Agents below within a minute.</li>
          </ol>
        </Card>
      )}

      <div className="grid kpi" style={{ marginBottom: 16 }}>
        <StatCard label="Agent" value={lastSeen ? ago(lastSeen) : 'never seen'}
          foot={lastSeen ? 'last poll' : 'install the agent on the Tally machine'}
          tone={!lastSeen || (Date.now() - new Date(lastSeen).getTime()) > 3600e3 ? 'attention' : undefined} />
        <StatCard label="Queued jobs" value={count(queued)} foot="waiting for the agent" />
        <StatCard label="Failed jobs" value={count(failed)} foot={failed ? 'retry them below' : 'nothing failed'}
          tone={failed ? 'attention' : undefined} />
        <StatCard label="Invoices not posted"
          value={activity.data ? count(Number(activity.data.pipeline.tally_pending) + Number(activity.data.pipeline.tally_failed)) : '—'}
          foot="issued but not yet in Tally" />
      </div>

      {activity.data && (
        <Card title="Sync activity" note="Jobs completed and failed, last 14 days">
          <ActivityBars data={activity.data.sync_activity} height={96} />
        </Card>
      )}

      {configs.loading && !configs.data && <CardSkeleton lines={4} />}
      {configs.data && configs.data.configs.map((c) => (
        <Card key={c.id} title={c.entity_name}
          note={c.tally_company ? `Selected in Tally: ${c.tally_company}` : 'The Connector has not reported a Tally company yet'}
          actions={(
            <>
              <Pill value={c.enabled ? 'active' : 'cancelled'} label={c.enabled ? 'syncing' : 'paused'} />
              {isAdmin && <button className="btn small" onClick={() => pairComputer(c.entity_id)}>Connect a Tally computer</button>}
              {isAdmin && <button className="btn small secondary" onClick={() => issueToken(c.entity_id)}
                title="For a hand-configured agent">Issue agent token</button>}
              <button className="btn small ghost" onClick={() => save(c.entity_id, { enabled: !c.enabled })}>
                {c.enabled ? 'Pause' : 'Resume'}
              </button>
            </>
          )}>
          <div className="row">
            <Field label="Sync frequency" hint="per client">
              <select value={c.interval_seconds} onChange={(e) => save(c.entity_id, { interval_seconds: Number(e.target.value) })}>
                {INTERVALS.map((s) => <option key={s} value={s}>{intervalLabel(s)}</option>)}
              </select>
            </Field>
            <Field label="Purchase lookback" hint="days of purchase vouchers to read">
              <input className="num" defaultValue={c.purchase_lookback_days}
                onBlur={(e) => {
                  const n = parseNumber(e.target.value);
                  if (n === null || n === Number(c.purchase_lookback_days)) return;
                  if (!Number.isInteger(n) || n < 0) { setError('Purchase lookback must be a whole number of days.'); return; }
                  save(c.entity_id, { purchase_lookback_days: n });
                }} />
            </Field>
            <Field label="Sales ledger in Tally">
              <input defaultValue={c.sales_ledger || ''} placeholder="Sales - Chemicals"
                onBlur={(e) => save(c.entity_id, { sales_ledger: e.target.value })} />
            </Field>
            <Field label="Voucher type">
              <input defaultValue={c.voucher_type || 'Sales'} onBlur={(e) => save(c.entity_id, { voucher_type: e.target.value })} />
            </Field>
          </div>

          <div className="row" style={{ marginTop: 4 }}>
            {FLAGS.map(([key, label, note]) => (
              <label key={key} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', color: 'var(--ink)', textTransform: 'none', letterSpacing: 0, fontWeight: 400 }}>
                <input type="checkbox" style={{ width: 16, marginTop: 3 }} checked={c[key]}
                  onChange={(e) => save(c.entity_id, { [key]: e.target.checked })} />
                <span><b style={{ fontWeight: 600 }}>{label}</b><div className="card-note">{note}</div></span>
              </label>
            ))}
          </div>

          <hr className="hairline" />
          <div className="card-note">
            Last pull {ago(c.last_pull_at)} · last push {ago(c.last_push_at)} · agent last seen {ago(c.agent_last_seen)} ·
            {' '}{c.queued} queued · {c.failed} failed
          </div>
          {c.last_error && <Alert kind="warn">{c.last_error}</Alert>}
        </Card>
      ))}

      {notPosted.data && notPosted.data.documents.length > 0 && (
        <Card title="Not posted to Tally"
          note="Posting is off for the company, or the agent gave up. Each stays deducted from stock until it is marked as entered by hand.">
          <div className="table-wrap">
            <table>
              <thead><tr><th>Document</th><th>Date</th><th>Buyer</th><th className="r">Value</th><th>Why</th><th /></tr></thead>
              <tbody>
                {notPosted.data.documents.map((d) => (
                  <tr key={`${d.doc_type}-${d.id}`}>
                    <td className="mono">
                      <Link to={d.doc_type === 'invoice' ? `/invoices/${d.id}` : `/credit-notes/${d.id}`}>{d.number}</Link>
                      <div className="sub">{d.doc_type === 'invoice' ? 'Invoice' : 'Credit note'} · {d.entity_name}</div>
                    </td>
                    <td>{day(d.date)}</td>
                    <td>{d.buyer_name}</td>
                    <td className="r num">{inr(d.grand_total, 0)}</td>
                    <td className="sub" style={{ maxWidth: 260 }}>
                      {d.tally_sync_status === 'skipped' ? 'Posting is off for this company' : (d.tally_error || 'The agent gave up after retrying')}
                    </td>
                    <td className="r"><MarkInTally compact docType={d.doc_type} id={d.id} onDone={notPosted.reload} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <Card title="Agents" note="One per Tally machine. Tokens are stored hashed and shown only once.">
        {agents.data && agents.data.agents.length === 0 && (
          <Empty title="No agent yet">{isAdmin ? 'Connect a Tally computer above' : 'An administrator connects one'}, then run the agent beside Ledger Shield on the Tally machine.</Empty>
        )}
        {agents.data && agents.data.agents.length > 0 && (
          <div className="table-wrap">
            <table>
              <thead><tr><th>Name</th><th>Entity</th><th>Token</th><th>Version</th><th>Last seen</th><th>IP</th><th>Tally link licence</th><th>State</th></tr></thead>
              <tbody>
                {agents.data.agents.map((a) => (
                  <tr key={a.id}>
                    <td>{a.name}</td><td>{a.entity_name}</td>
                    <td className="mono">{a.token_prefix}…</td>
                    <td className="num">{a.version || '—'}</td>
                    <td>{a.last_seen_at ? ago(a.last_seen_at) : 'never'}</td>
                    <td className="mono">{a.last_ip || '—'}</td>
                    <td title={a.ls_machine_id || ''}>
                      {a.ls_licence_expires_at
                        ? (new Date(a.ls_licence_expires_at) > new Date() ? `to ${stamp(a.ls_licence_expires_at)}` : `lapsed ${ago(a.ls_licence_expires_at)}`)
                        : 'not issued yet'}
                    </td>
                    <td><Pill value={a.is_active ? 'active' : 'cancelled'} label={a.is_active ? 'active' : 'disabled'} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card title="Job queue" note="Most recent first"
        actions={(
          <>
            <div className="segmented" role="group" aria-label="Job filter">
              {JOB_VIEWS.map(([key, label]) => (
                <button key={label} aria-pressed={jobView === key} onClick={() => setJobView(key)}>{label}</button>
              ))}
            </div>
            <button className="btn small ghost" onClick={jobs.reload}>Refresh</button>
          </>
        )}>
        <Alert onRetry={jobs.reload}>{jobs.error}</Alert>
        {jobs.data && jobs.data.jobs.length === 0 && <Empty title="The queue is empty">Work appears here as the cadence comes round.</Empty>}
        {jobs.data && jobs.data.jobs.length > 0 && (
          <div className="table-wrap">
            <table>
              <thead><tr><th>Job</th><th>Status</th><th>Invoice</th><th className="c">Attempts</th><th>When</th><th>Result</th><th /></tr></thead>
              <tbody>
                {jobs.data.jobs.map((j) => (
                  <tr key={j.id}>
                    <td>{j.kind.replace(/_/g, ' ')}</td>
                    <td><Pill value={j.status} /></td>
                    <td className="mono">{j.invoice_number || '—'}</td>
                    <td className="c num">{j.attempts}/{j.max_attempts}</td>
                    <td>{stamp(j.updated_at)}</td>
                    <td className="sub" style={{ maxWidth: 320 }}>{j.last_error || (j.result ? JSON.stringify(j.result) : '')}</td>
                    <td className="r">
                      {j.status === 'failed' && (
                        <button className="btn small secondary"
                          onClick={async () => {
                            setError(null);
                            try { await api.post(`/api/sync/jobs/${j.id}/retry`); jobs.reload(); }
                            catch (e) { setError(e.message); }
                          }}>Retry</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {jobs.data && <LoadMore list={jobs} />}
      </Card>

      <Card title="Recent events">
        {events.data && (
          <div className="table-wrap">
            <table>
              <thead><tr><th>When</th><th>Level</th><th>Message</th><th>Detail</th></tr></thead>
              <tbody>
                {events.data.events.map((e) => (
                  <tr key={e.id}>
                    <td>{stamp(e.created_at)}</td>
                    <td><Pill value={e.level === 'error' ? 'failed' : 'done'} label={e.level} /></td>
                    <td>{e.message}</td>
                    <td className="sub mono" style={{ maxWidth: 360 }}>{e.data ? JSON.stringify(e.data) : ''}</td>
                  </tr>
                ))}
                {events.data.events.length === 0 && (
                  <tr><td colSpan={4}><Empty title="Nothing logged yet">Sync events appear here as the agent works.</Empty></td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  );
}
